import { motion } from "framer-motion";
import { Compass, Layers, Check } from "lucide-react";
import Parallax from "./Parallax";

const PHOTO = "/assets/img/photo-band.jpg";

const YOU_LEAD = [
  "Role priorities and hiring decisions",
  "The work and how it is delivered",
  "Quality standards and definition of done",
  "Team culture and ways of working",
  "Strategic direction for the India team",
];

const EXPANTE_PROVIDES = [
  "Local employment and payroll administration",
  "Compliance administration for the engagement",
  "Infrastructure and operational setup",
  "Day-to-day local coordination",
  "A dedicated local point of contact",
];

const Panel = ({ title, sub, items, icon: Icon, accent, testid, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={`card-jeton relative h-full rounded-[28px] p-8 sm:p-10 hover:rotate-0 ${
      accent
        ? "bg-[#5B4199] shadow-[0_28px_64px_-18px_rgba(91,65,153,0.5)] lg:rotate-1"
        : "bg-[#F2EDFE] shadow-[0_24px_56px_-18px_rgba(91,65,153,0.2)] lg:-rotate-1"
    }`}
    data-testid={testid}
  >
    <div className="flex items-center gap-3.5 mb-8">
      <span
        className={`flex items-center justify-center h-12 w-12 rounded-2xl ${
          accent ? "bg-white/15 text-white" : "bg-white text-[#5B4199] shadow-sm"
        }`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <p className={`text-[10px] font-mono-tech uppercase tracking-[0.2em] ${accent ? "text-white/60" : "text-[#94A3B8]"}`}>{sub}</p>
        <h3 className={`font-display text-xl sm:text-2xl font-extrabold ${accent ? "text-white" : "text-[#0B132B]"}`}>{title}</h3>
      </div>
    </div>
    <ul className="space-y-4">
      {items.map((item) => (
        <li key={item} className={`flex items-start gap-3 text-[15px] leading-relaxed ${accent ? "text-white/90" : "text-[#475569]"}`}>
          <span className={`flex h-5 w-5 mt-0.5 shrink-0 items-center justify-center rounded-full ${accent ? "bg-white/20" : "bg-white shadow-sm"}`}>
            <Check className={`h-3 w-3 ${accent ? "text-white" : "text-[#5B4199]"}`} />
          </span>
          {item}
        </li>
      ))}
    </ul>
  </motion.div>
);

export default function PartnerSplit() {
  return (
    <section id="partner" className="relative -mt-8 rounded-t-[48px] py-20 sm:py-28 lg:py-32 bg-white" data-testid="partner-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-14"
        >
          <span className="inline-flex items-center rounded-full border border-[#5B4199]/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#5B4199] mb-6">
            The proposition
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-7xl font-extrabold tracking-tight text-[#5B4199] leading-[1.04]">
            Local execution. Your operating standards.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#475569] leading-relaxed">
            Build an India team that operates as an extension of your business—not as a separate
            delivery function. You set the priorities, standards, culture, and direction. Expante
            manages the local employment, infrastructure, compliance administration, and operational
            coordination that keep the team running.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          <Parallax distance={30} className="h-full">
            <Panel title="Your business leads" sub="Client side" items={YOU_LEAD} icon={Compass} testid="panel-you-lead" delay={0} />
          </Parallax>
          <Parallax distance={-24} className="h-full">
            <Panel
              title="Expante supports"
              sub="Expante side"
              items={EXPANTE_PROVIDES}
              icon={Layers}
              accent
              testid="panel-expante-provides"
              delay={0.12}
            />
          </Parallax>
        </div>

        <Parallax distance={44}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-14 relative rounded-[32px] overflow-hidden shadow-[0_32px_70px_-24px_rgba(11,19,43,0.3)]"
            data-testid="partner-photo-band"
          >
            <img src={PHOTO} alt="A connected India team at work" className="w-full h-[300px] sm:h-[380px] object-cover object-[center_25%]" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/60 via-transparent to-transparent" />
            <p className="absolute bottom-6 left-7 text-white font-display text-lg sm:text-xl font-bold">
              Local execution. Your operating standards.
            </p>
          </motion.div>
        </Parallax>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-8 text-sm text-[#94A3B8] max-w-2xl"
        >
          Build an India team that operates as an extension of your business—not as a separate
          delivery function. You set the priorities, standards, culture, and direction. Expante
          manages the local employment, infrastructure, compliance administration, and operational
          coordination that keep the team running.
        </motion.p>
      </div>
    </section>
  );
}
